import React from 'react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { ButtonGroupProps } from './ButtonGroup.types';

function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * ButtonGroup - Grupo de botones toggle conectados
 *
 * Componente de selección tipo radio donde los botones aparecen
 * visualmente conectados. Solo un valor puede estar seleccionado a la vez.
 * Varianite para entorno de Playground.
 *
 * Soporta tres modos por opción:
 * - **Solo texto**: `{ value, label }`
 * - **Icono + texto**: `{ value, label, icon }`
 * - **Solo icono**: `{ value, icon, ariaLabel }` (botón cuadrado)
 *
 * **Figma:** Button Group Item
 * Node ID: 7879-38523
 *
 * @example
 * ```tsx
 * // Solo texto
 * <ButtonGroup
 *   options={[
 *     { value: 'con', label: 'Con' },
 *     { value: 'sin', label: 'Sin' },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 *
 * // Icono + texto
 * <ButtonGroup
 *   options={[
 *     { value: 'playground', label: 'Playground', icon: <StarIcon /> },
 *     { value: 'identified', label: 'Identificado' },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 *
 * // Solo icono
 * <ButtonGroup
 *   options={[
 *     { value: 'list', icon: <ListIcon />, ariaLabel: 'Vista lista' },
 *     { value: 'grid', icon: <GridIcon />, ariaLabel: 'Vista cuadrícula' },
 *   ]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 * ```
 *
 * @see https://www.figma.com/design/5XNqf2YTxvwemxwo1LMQ6j/Siesa-UI-Kit?node-id=7879-38523
 */
export function ButtonGroup({
  options,
  value,
  onChange,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: ButtonGroupProps) {
  return (
    <div
      className={cn('inline-flex', className)}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option, index) => {
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const isSelected = value === option.value;
        const isDisabled = disabled || option.disabled;
        const isIconOnly = option.icon && !option.label;
        const iconPos = option.iconPosition || 'left';

        const iconElement = option.icon ? (
          <span className={cn(
            "inline-flex items-center justify-center w-4 h-4 transition-colors duration-150",
            !isSelected && "text-zinc-400 dark:text-zinc-400 group-hover:text-primary"
          )}>
            {option.icon}
          </span>
        ) : null;

        const labelElement = option.label ? (
          <span className="text-sm">{option.label}</span>
        ) : null;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-label={option.ariaLabel || option.label}
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(option.value)}
            className={cn(
              'group',
              'flex items-center justify-center',
              'text-sm leading-5',
              'border',
              'transition-all duration-150',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-1',
              isIconOnly ? 'p-1.5' : 'px-3.5 py-1.5 gap-1.5',
              isFirst && 'rounded-l',
              isLast && 'rounded-r',
              isSelected && [
                'font-medium',
                'bg-primary-100 border-primary-500 text-primary',              
                'dark:bg-primary-950 dark:border-primary-300 dark:text-primary-300',
              ],
              !isSelected && [
                'font-normal',
                'bg-white border-zinc-300 text-zinc-900',           
                'hover:bg-primary-100 hover:border-primary-500 hover:text-primary',
                'dark:bg-zinc-900 dark:border-zinc-600 dark:text-zinc-100 dark:hover:bg-primary-950 dark:hover:border-primary-300 dark:hover:text-primary-300',
              ],
              isDisabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {iconPos === 'left' && iconElement}
            {labelElement}
            {iconPos === 'right' && iconElement}
          </button>
        );
      })}
    </div>
  );
}

export default ButtonGroup;
